import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

// 커서를 이만큼 머물러야 연다
export const SIDEBAR_PEEK_OPEN_DELAY_MS = 300;
// 트리거에서 띄운 패널로 커서를 옮기는 사이(상단 바 토글 아래 여백 등)에 닫히지 않게 두는 유예
export const SIDEBAR_PEEK_CLOSE_DELAY_MS = 150;

// 열림 여부는 React state 가 아니라 여기 둔다 — 훅을 든 쪽(상단 바 토글이면 페이지 최상단)이
// hover 마다 다시 렌더되지 않고, 읽는 쪽(DocumentSidebar)만 useSidebarPeekOpen 으로 구독한다
const createPeekStore = () => {
  let open = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const listeners = new Set<() => void>();

  const set = (next: boolean) => {
    if (open === next) return;
    open = next;
    listeners.forEach((listener) => listener());
  };
  const clearTimer = () => {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
  };

  return {
    getOpen: () => open,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    schedule: (next: boolean, delay: number) => {
      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        set(next);
      }, delay);
    },
    reset: () => {
      clearTimer();
      set(false);
    },
  };
};

/**
 * 접힌 목차의 hover 미리보기(본문 위에 띄우기) — 목차 미리보기의 유일한 구현.
 * 트리거(icon 레일이면 패널 자체, offcanvas 면 상단 바 토글)와 띄운 패널이 enter/leave 를 부른다:
 * 둘 중 하나에 커서가 있는 동안 열려 있다.
 * 접힘/펼침이 바뀌면 지운다 — 토글 클릭·⌘B 어느 경로로 바뀌어도 미리보기가 남지 않게. 펼친 동안의 enter 는 무시한다.
 */
export const useSidebarPeek = (collapsed: boolean) => {
  const [store] = useState(createPeekStore);
  useEffect(() => store.reset, [store]);
  useEffect(() => store.reset(), [collapsed, store]);

  const enter = useCallback(() => {
    if (collapsed) store.schedule(true, SIDEBAR_PEEK_OPEN_DELAY_MS);
  }, [collapsed, store]);
  const leave = useCallback(
    () => store.schedule(false, SIDEBAR_PEEK_CLOSE_DELAY_MS),
    [store],
  );

  return useMemo(() => ({ store, enter, leave }), [store, enter, leave]);
};

export type SidebarPeek = ReturnType<typeof useSidebarPeek>;

/** 미리보기 열림 여부 구독 — 이걸 부른 컴포넌트만 다시 렌더된다 */
export const useSidebarPeekOpen = ({ store }: SidebarPeek) =>
  useSyncExternalStore(store.subscribe, store.getOpen);
